import * as React from "react"
import { any, mk_widget, Widget } from "widgets-for-react"
import { styleguideChapter, styleguideSection, styleguideTokenPart, styleguideTokenRow } from "../helpers"

export const styleguideTokens: Widget<never> = mk_widget({
  run: _ =>
    styleguideChapter({
      type: 'tokens',
      slug: 'tokens',
      title: 'Design Tokens',
      intro:
        'Design tokens are the visual design atoms of the design system — specifically, they are named entities that store visual design attributes. We use them in place of hard-coded values (such as hex values for color or pixel values for spacing) in order to maintain a scalable and consistent visual system for UI development.'
    })(
      any()([
        styleguideSection({
          type: 'tokens',
          slug: 'tokens-colors',
          title: 'Colors',
          intro: 'Please refer to the description of each token for their intended usage.'
        })(
          styleguideTokenPart({ hasExampleColumn: true, hasAttributeColumn: true })(
            any()([
              styleguideTokenRow({
                code: '$color--white',
                description: '',
                attributes: '#FFFFFF',
                columnCount: 3,
                hasSeperator: false,
                hasExample: true
              })(
                mk_widget({
                  run: _ => <div className="styleguide-token__color token-color--FFFFFF"></div>
                })
              ),
              styleguideTokenRow({
                code: '$color--grey-100',
                description: '',
                attributes: '#F9F9F9',
                columnCount: 3,
                hasSeperator: false,
                hasExample: true
              })(
                mk_widget({
                  run: _ => <div className="styleguide-token__color token-color--F9F9F9"></div>
                })
              ),
              styleguideTokenRow({
                code: '$color--grey-300',
                description: '',
                attributes: '#D8D8D8',
                columnCount: 3,
                hasSeperator: false,
                hasExample: true
              })(
                mk_widget({
                  run: _ => <div className="styleguide-token__color token-color--D8D8D8"></div>
                })
              ),
              styleguideTokenRow({
                code: '$color--grey-500',
                description: '',
                attributes: '#4C4C4C',
                columnCount: 3,
                hasSeperator: false,
                hasExample: true
              })(
                mk_widget({
                  run: _ => <div className="styleguide-token__color token-color--4C4C4C"></div>
                })
              ),
              styleguideTokenRow({
                code: '$color--grey-700',
                description: '',
                attributes: '#323F48',
                columnCount: 3,
                hasSeperator: true,
                hasExample: true
              })(
                mk_widget({
                  run: _ => <div className="styleguide-token__color token-color--323F48"></div>
                })
              ),
              styleguideTokenRow({
                code: '$color--blue-200',
                description: '',
                attributes: '#00d7e6',
                columnCount: 3,
                hasSeperator: false,
                hasExample: true
              })(
                mk_widget({
                  run: _ => <div className="styleguide-token__color token-color--00d7e6"></div>
                })
              ),
              styleguideTokenRow({
                code: '$color--blue-500',
                description: '',
                attributes: '#0084D1',
                columnCount: 3,
                hasSeperator: false,
                hasExample: true
              })(
                mk_widget({
                  run: _ => <div className="styleguide-token__color token-color--0084D1"></div>
                })
              ),
              styleguideTokenRow({
                code: '$color--blue-700',
                description: '',
                attributes: '#0077c8',
                columnCount: 3,
                hasSeperator: true,
                hasExample: true
              })(
                mk_widget({
                  run: _ => <div className="styleguide-token__color token-color--0077c8"></div>
                })
              ),
              styleguideTokenRow({
                code: '$color--orange-500',
                description: '',
                attributes: '#EE7D11',
                columnCount: 3,
                hasSeperator: false,
                hasExample: true
              })(
                mk_widget({
                  run: _ => <div className="styleguide-token__color token-color--EE7D11"></div>
                })
              ),
              styleguideTokenRow({
                code: '$color--orange-700',
                description: '',
                attributes: '#C3670E',
                columnCount: 3,
                hasSeperator: false,
                hasExample: true
              })(
                mk_widget({
                  run: _ => <div className="styleguide-token__color token-color--C3670E"></div>
                })
              )
            ])
          )
        ),
        styleguideSection({ type: 'tokens', slug: 'tokens-fonts', title: 'Fonts', intro: '' })(
          styleguideTokenPart({ hasExampleColumn: true, hasAttributeColumn: true })(
            any()([
              styleguideTokenRow({
                code: '$styleguide-font--pt-sans',
                description: '',
                attributes: "'PT Sans', sans-serif",
                columnCount: 3,
                hasSeperator: false,
                hasExample: true
              })(
                mk_widget({
                  run: _ => (
                    <p>
                      Vulputate Commodo Sollicitudin Fringilla Nullam. Etiam porta sem malesuada magna mollis euismod.
                    </p>
                  )
                })
              )
            ])
          )
        ),
        styleguideSection({ type: 'tokens', slug: 'tokens-styles', title: 'Font Styles', intro: '' })(
          styleguideTokenPart({ hasExampleColumn: true, hasAttributeColumn: true })(
            any()([
              styleguideTokenRow({
                code: '$weight--regular',
                description: '',
                attributes: 'Font-weight: 400',
                columnCount: 3,
                hasSeperator: false,
                hasExample: true
              })(
                mk_widget({
                  run: _ => (
                    <p className="token-weight--400">
                      Vulputate Commodo Sollicitudin Fringilla Nullam. Etiam porta sem malesuada magna mollis euismod.
                    </p>
                  )
                })
              ),
              styleguideTokenRow({
                code: '$weight--bold',
                description: '',
                attributes: 'Font-weight: 700',
                columnCount: 3,
                hasSeperator: true,
                hasExample: true
              })(
                mk_widget({
                  run: _ => (
                    <p className="token-weight--700">
                      Vulputate Commodo Sollicitudin Fringilla Nullam. Etiam porta sem malesuada magna mollis euismod.
                    </p>
                  )
                })
              ),
              styleguideTokenRow({
                code: '$style--italic',
                description: '',
                attributes: 'Font-style: italic',
                columnCount: 3,
                hasSeperator: false,
                hasExample: true
              })(
                mk_widget({
                  run: _ => (
                    <p className="token-style--italic">
                      Vulputate Commodo Sollicitudin Fringilla Nullam. Etiam porta sem malesuada magna mollis euismod.
                    </p>
                  )
                })
              )
            ])
          )
        ),
        styleguideSection({
          type: 'tokens',
          slug: 'tokens-sizes-heights',
          title: 'Font Sizes & Line Heights',
          intro: ''
        })(
          styleguideTokenPart({ hasExampleColumn: true, hasAttributeColumn: true })(
            any()([
              styleguideTokenRow({
                code: '%text--s',
                description: '',
                attributes: ['Font-size: 14px', `Line-height: 24px / ${24 / 14}`],
                columnCount: 3,
                hasSeperator: false,
                hasExample: true
              })(
                mk_widget({
                  run: _ => (
                    <p className="text--s">
                      Vulputate Commodo Sollicitudin Fringilla Nullam. Etiam porta sem malesuada magna mollis euismod.
                    </p>
                  )
                })
              ),
              styleguideTokenRow({
                code: '%text--m',
                description: '',
                attributes: ['Font-size: 16px', `Line-height: 24px / ${24 / 16}`],
                columnCount: 3,
                hasSeperator: false,
                hasExample: true
              })(
                mk_widget({
                  run: _ => (
                    <p className="text--m">
                      Vulputate Commodo Sollicitudin Fringilla Nullam. Etiam porta sem malesuada magna mollis euismod.
                    </p>
                  )
                })
              ),
              styleguideTokenRow({
                code: '%text--l',
                description: '',
                attributes: ['Font-size: 18px', `Line-height: 36px / ${36 / 18}`],
                columnCount: 3,
                hasSeperator: true,
                hasExample: true
              })(
                mk_widget({
                  run: _ => (
                    <p className="text--l">
                      Vulputate Commodo Sollicitudin Fringilla Nullam. Etiam porta sem malesuada magna mollis euismod.
                    </p>
                  )
                })
              ),
              styleguideTokenRow({
                code: '%title--xxs',
                description: '',
                attributes: ['Font-size: 14px', `Line-height: 30px / ${30 / 20}`],
                columnCount: 3,
                hasSeperator: false,
                hasExample: true
              })(
                mk_widget({
                  run: _ => <h3 className="title--xxs">Vulputate Commodo Sollicitudin Fringilla Nullam</h3>
                })
              ),
              styleguideTokenRow({
                code: '%title--xs',
                description: '',
                attributes: ['Font-size: 20px', `Line-height: 30px / ${30 / 20}`],
                columnCount: 3,
                hasSeperator: false,
                hasExample: true
              })(
                mk_widget({
                  run: _ => <h3 className="title--xs">Vulputate Commodo Sollicitudin Fringilla Nullam</h3>
                })
              ),
              styleguideTokenRow({
                code: '%title--s',
                description: '',
                attributes: ['Font-size: 20px > 25px', `Line-height: 30px / ${30 / 20}`],
                columnCount: 3,
                hasSeperator: false,
                hasExample: true
              })(
                mk_widget({
                  run: _ => <h3 className="title--s">Vulputate Commodo Sollicitudin Fringilla Nullam</h3>
                })
              ),
              styleguideTokenRow({
                code: '%title--m',
                description: '',
                attributes: ['Font-size: 30px > 35px', `Line-height: 30px / ${30 / 24}`],
                columnCount: 3,
                hasSeperator: false,
                hasExample: true
              })(
                mk_widget({
                  run: _ => <h2 className="title--m">Vulputate Commodo Sollicitudin Fringilla Nullam</h2>
                })
              ),
              styleguideTokenRow({
                code: '%title--l',
                description: '',
                attributes: ['Font-size: 35px > 55px', `Line-height: 30px / ${30 / 24}`],
                columnCount: 3,
                hasSeperator: false,
                hasExample: true
              })(
                mk_widget({
                  run: _ => <h2 className="title--l">Vulputate Commodo Sollicitudin Fringilla Nullam</h2>
                })
              ),
              styleguideTokenRow({
                code: '%title--xl',
                description: '',
                attributes: ['Font-size: 48px > 70px', `Line-height: 58px / ${58 / 48}`],
                columnCount: 3,
                hasSeperator: true,
                hasExample: true
              })(
                mk_widget({
                  run: _ => <h1 className="title--xl">Vulputate Commodo Sollicitudin Fringilla Nullam</h1>
                })
              )
            ])
          )
        ),
        styleguideSection({ type: 'tokens', slug: 'tokens-icons', title: 'Icons', intro: '' })(
          styleguideTokenPart({ hasExampleColumn: true, hasAttributeColumn: false })(
            any()([
              styleguideTokenRow({
                code: '#icon--chat',
                description: '',
                attributes: '',
                columnCount: 2,
                hasSeperator: false,
                hasExample: true
              })(
                mk_widget({
                  run: _ => (
                    <svg className="styleguide-token__icon" width="14" height="13">
                      <use xlinkHref="#icon--chat" />
                    </svg>
                  )
                })
              ),
              styleguideTokenRow({
                code: '#icon--arrow',
                description: '',
                attributes: '',
                columnCount: 2,
                hasSeperator: false,
                hasExample: true
              })(
                mk_widget({
                  run: _ => (
                    <svg className="styleguide-token__icon" width="14" height="14px">
                      <use xlinkHref="#icon--arrow" />
                    </svg>
                  )
                })
              ),
              styleguideTokenRow({
                code: '#icon--check',
                description: '',
                attributes: '',
                columnCount: 2,
                hasSeperator: false,
                hasExample: true
              })(
                mk_widget({
                  run: _ => (
                    <svg className="styleguide-token__icon" width="12" height="9px">
                      <use xlinkHref="#icon--check" />
                    </svg>
                  )
                })
              ),
              styleguideTokenRow({
                code: '#icon--download',
                description: '',
                attributes: '',
                columnCount: 2,
                hasSeperator: false,
                hasExample: true
              })(
                mk_widget({
                  run: _ => (
                    <svg className="styleguide-token__icon" width="14" height="14">
                      <use xlinkHref="#icon--download" />
                    </svg>
                  )
                })
              ),
              styleguideTokenRow({
                code: '#icon--caret',
                description: '',
                attributes: '',
                columnCount: 2,
                hasSeperator: false,
                hasExample: true
              })(
                mk_widget({
                  run: _ => (
                    <svg className="styleguide-token__icon" width="6" height="8">
                      <use xlinkHref="#icon--caret" />
                    </svg>
                  )
                })
              ),
              styleguideTokenRow({
                code: '#icon--date',
                description: '',
                attributes: '',
                columnCount: 2,
                hasSeperator: false,
                hasExample: true
              })(
                mk_widget({
                  run: _ => (
                    <svg className="styleguide-token__icon" width="13" height="13">
                      <use xlinkHref="#icon--date" />
                    </svg>
                  )
                })
              ),
              styleguideTokenRow({
                code: '#icon--person',
                description: '',
                attributes: '',
                columnCount: 2,
                hasSeperator: false,
                hasExample: true
              })(
                mk_widget({
                  run: _ => (
                    <svg className="styleguide-token__icon" width="10" height="10">
                      <use xlinkHref="#icon--person" />
                    </svg>
                  )
                })
              ),
              styleguideTokenRow({
                code: '#icon--marker',
                description: '',
                attributes: '',
                columnCount: 2,
                hasSeperator: false,
                hasExample: true
              })(
                mk_widget({
                  run: _ => (
                    <svg className="styleguide-token__icon" width="10" height="13">
                      <use xlinkHref="#icon--marker" />
                    </svg>
                  )
                })
              ),
              styleguideTokenRow({
                code: '#icon--search',
                description: '',
                attributes: '',
                columnCount: 2,
                hasSeperator: false,
                hasExample: true
              })(
                mk_widget({
                  run: _ => (
                    <svg className="styleguide-token__icon" width="15" height="15">
                      <use xlinkHref="#icon--search" />
                    </svg>
                  )
                })
              ),
              styleguideTokenRow({
                code: '#icon--cart',
                description: '',
                attributes: '',
                columnCount: 2,
                hasSeperator: false,
                hasExample: true
              })(
                mk_widget({
                  run: _ => (
                    <svg className="styleguide-token__icon" width="10" height="9">
                      <use xlinkHref="#icon--cart" />
                    </svg>
                  )
                })
              ),
              styleguideTokenRow({
                code: '#icon--phone',
                description: '',
                attributes: '',
                columnCount: 2,
                hasSeperator: false,
                hasExample: true
              })(
                mk_widget({
                  run: _ => (
                    <svg className="styleguide-token__icon" width="8" height="14">
                      <use xlinkHref="#icon--phone" />
                    </svg>
                  )
                })
              ),
              styleguideTokenRow({
                code: '#icon--mail',
                description: '',
                attributes: '',
                columnCount: 2,
                hasSeperator: true,
                hasExample: true
              })(
                mk_widget({
                  run: _ => (
                    <svg className="styleguide-token__icon" width="12" height="9">
                      <use xlinkHref="#icon--mail" />
                    </svg>
                  )
                })
              ),
              styleguideTokenRow({
                code: '#icon--facebook',
                description: '',
                attributes: '',
                columnCount: 2,
                hasSeperator: false,
                hasExample: true
              })(
                mk_widget({
                  run: _ => (
                    <svg className="styleguide-token__icon" width="12" height="9">
                      <use xlinkHref="#icon--facebook" />
                    </svg>
                  )
                })
              ),
              styleguideTokenRow({
                code: '#icon--linkedin',
                description: '',
                attributes: '',
                columnCount: 2,
                hasSeperator: false,
                hasExample: true
              })(
                mk_widget({
                  run: _ => (
                    <svg className="styleguide-token__icon" width="12" height="9">
                      <use xlinkHref="#icon--linkedin" />
                    </svg>
                  )
                })
              ),
              styleguideTokenRow({
                code: '#icon--twitter',
                description: '',
                attributes: '',
                columnCount: 2,
                hasSeperator: false,
                hasExample: true
              })(
                mk_widget({
                  run: _ => (
                    <svg className="styleguide-token__icon" width="12" height="9">
                      <use xlinkHref="#icon--twitter" />
                    </svg>
                  )
                })
              )
            ])
          )
        ),
        styleguideSection({ type: 'tokens', slug: 'tokens-widths', title: 'Widths', intro: '' })(
          styleguideTokenPart({ hasExampleColumn: false, hasAttributeColumn: true })(
            any()([
              styleguideTokenRow({
                code: '$width--s',
                description: '',
                attributes: '860 pixels',
                columnCount: 2,
                hasSeperator: false,
                hasExample: false
              })(mk_widget({ run: _ => null })),
              styleguideTokenRow({
                code: '$width--s-gutter',
                description: '',
                attributes: ['940 pixels', 'Gutter: 2x 40 pixels'],
                columnCount: 2,
                hasSeperator: false,
                hasExample: false
              })(mk_widget({ run: _ => null })),
              styleguideTokenRow({
                code: '$width--m',
                description: '',
                attributes: '940 pixels',
                columnCount: 2,
                hasSeperator: false,
                hasExample: false
              })(mk_widget({ run: _ => null })),
              styleguideTokenRow({
                code: '$width--m-gutter',
                description: '',
                attributes: ['1020 pixels', 'Gutter: 2x 40 pixels'],
                columnCount: 2,
                hasSeperator: false,
                hasExample: false
              })(mk_widget({ run: _ => null })),
              styleguideTokenRow({
                code: '$width--l',
                description: '',
                attributes: '1080 pixels',
                columnCount: 2,
                hasSeperator: false,
                hasExample: false
              })(mk_widget({ run: _ => <></> })),
              styleguideTokenRow({
                code: '$width--l-gutter',
                description: '',
                attributes: ['1160 pixels', 'Gutter: 2x 40 pixels'],
                columnCount: 2,
                hasSeperator: false,
                hasExample: false
              })(mk_widget({ run: _ => null }))
            ])
          )
        ),
        styleguideSection({
          type: 'tokens',
          slug: 'tokens-spacings-gutters',
          title: 'Spacings & Gutters',
          intro:
            "The 'spacing'-variables are used for vertical spacing, while the 'gutter'-variables are used for horizontal spacing."
        })(
          styleguideTokenPart({ hasExampleColumn: false, hasAttributeColumn: true })(
            any()([
              styleguideTokenRow({
                code: '$spacing--s',
                description: '',
                attributes: '30 pixels',
                columnCount: 2,
                hasSeperator: false,
                hasExample: false
              })(mk_widget({ run: _ => null })),
              styleguideTokenRow({
                code: '$spacing--m',
                description: '',
                attributes: '40 pixels',
                columnCount: 2,
                hasSeperator: false,
                hasExample: false
              })(mk_widget({ run: _ => null })),
              styleguideTokenRow({
                code: '$spacing--l',
                description: '',
                attributes: '60 pixels',
                columnCount: 2,
                hasSeperator: true,
                hasExample: false
              })(mk_widget({ run: _ => null })),
              styleguideTokenRow({
                code: '$gutter--s',
                description: '',
                attributes: '20 pixels',
                columnCount: 2,
                hasSeperator: false,
                hasExample: false
              })(mk_widget({ run: _ => null })),
              styleguideTokenRow({
                code: '$gutter--m',
                description: '',
                attributes: '30 pixels',
                columnCount: 2,
                hasSeperator: false,
                hasExample: false
              })(mk_widget({ run: _ => null })),
              styleguideTokenRow({
                code: '$gutter--l',
                description: '',
                attributes: '40 pixels',
                columnCount: 2,
                hasSeperator: false,
                hasExample: false
              })(mk_widget({ run: _ => null }))
            ])
          )
        ),
        styleguideSection({ type: 'tokens', slug: 'tokens-transitions', title: 'Transitions', intro: '' })(
          styleguideTokenPart({ hasExampleColumn: false, hasAttributeColumn: true })(
            any()([
              styleguideTokenRow({
                code: '$transition--normal',
                description: 'o.a. used for all transitions at the moment.',
                attributes: '0.3 seconds',
                columnCount: 2,
                hasSeperator: false,
                hasExample: false
              })(mk_widget({ run: _ => null }))
            ])
          )
        )
      ])
    ).run(_)
})
